<template>
    <b-card no-body class="report-card">

        <template v-slot:header>
            <report-input-header
                v-if="reportLoaded"
                :report="report"
                :online="isOnline"
                :saving-row="savingRow"
                :stats="stats"
                :queue="queueLength"
                :is-new="isNewReport"
                :last-save-date="lastSaveDate"
                :active-filters="activeFilters"
                @clearFilters="clearFilters"
                @synchronized="postSync"
            ></report-input-header>
        </template>

        <div class="card-body" id="report-table-wrapper">
            <loading-spinner class="loading-spinner" v-if="!reportLoaded" />

            <div id="handonstable-container">
                <div ref="ht">
                    <div
                        v-if="reportLoaded"
                        class="scrollback"
                        :title="$t('scroll_back')"
                        @click="scrollback()"
                    ></div>
                </div>
            </div>
        </div>

        <!-- Modal update report -->
        <div
            class="modal fade"
            :class="{ show: fileManagerVisible }"
            tabindex="-1"
            role="dialog"
            id="file-manager-modal"
            style="z-index: 1040"
            aria-labelledby="myLargeModalLabel"
            :style="{ display: fileManagerVisible ? 'block' : 'none' }"
            aria-hidden="true"
        >
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title" id="myLargeModalLabel">
                            {{ fileManagerTitle }}
                        </h4>
                        <button
                            type="button"
                            class="close"
                            @click="fileManagerVisible = false"
                            aria-label="Close"
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <file-manager
                            v-if="hotInstance && fileManagerResourceId"
                            resource-type="report-row"
                            :resource-id="fileManagerResourceId"
                            @updated-files="filesUpdated"
                            @uploading-started="uploadingStarted"
                            @uploading-finished="uploadingFinished"
                        />
                    </div>
                </div>
            </div>
        </div>
    </b-card>
</template>

<style lang="scss" scoped>
.report-card .card-header {
  background-color: #ffffff;
}

#report-table-wrapper {
  padding: unset;
  height: calc(100vh - 180px);
}
</style>

<script>
import _ from "lodash";
import axios from "axios";
import moment from "moment";
import offline from "offline";
import { v4 as uuidv4 } from "uuid";
import Handsontable from "handsontable";
import "handsontable/dist/handsontable.full.css";
import { memoizeDebounce } from "../../plugins/utilities";
import { Queue } from "../../Queue";

import LoadingSpinner from "../Common/LoadingSpinner";
import FileManager from "../Common/FileManager";
import ReportInputHeader from "./InputHeader";

// @todo question: do we need to limit the collections to only return collections that have base products that have variants?
// @todo question: do we need to limit the base products to only show base product that have a variant?

let commandStack = new Queue();

window.addEventListener("beforeunload", event => {
  if (commandStack.count()) {
    event.preventDefault();
    event.returnValue = this.$t("exit_page_confirmation");
    return this.$t("exit_page_confirmation");
  }
});

export default {
  name: "Input",
  components: {
    FileManager,
    LoadingSpinner,
    ReportInputHeader
  },
  props: {
    // Get the report, template and client
    reportId: {
      type: String,
      default: null
    }
  },
  data: function() {
    let ctx = this;
    return {
      stoneColumnNames: [],
      problemRepossiDb: [],
      isDepartments: false,
      isShowSerialNumber: false,
      isNewReport: true,
      pass: 0,
      fail: 0,
      data: [],
      activeFilters: 0,
      successRate: 0,
      hotInstance: null,
      report: null,
      reportLoaded: false,
      savingRow: false,
      deletingRow: false,
      lastSaveDate: null,
      isOnline: !offline(),
      queueLength: 0,
      products: [],
      backendErrors: [],
      fileManagerResourceId: null,
      fileManagerTitle: "FileManager",
      fileManagerVisible: false
    };
  },
  computed: {
    stats() {
      return {
        pass: this.pass,
        fail: this.fail,
        successRate: this.successRate
      };
    },
    productAttributes() {
      let attributes = [];

      this.data.forEach(r => {
        Object.keys(r.product.product_attributes).forEach(k => {
          let attribute = r.product.product_attributes[k];
          if (attributes.find(e => e.key === k) !== undefined) {
            return;
          }

          attributes.push({
            key: k,
            name:
              attribute.attr.charAt(0).toUpperCase() + attribute.attr.slice(1)
          });
        });
      });
      return attributes;
    }
  },
  mounted() {
    // Collapse sidebar menu
    this.$root.$emit("collapseSidebar", true);

    // Loading multiple resources that are needed to bootstrap the report
    // when all these resources have been loaded, the report is Loaded and can
    // be rendered.

    this.getProblemRepossiDb();
    // We also need to load the data to populate the existing report here...
    this.getReport()
      .then(this.fetchProblems)
      .then(this.getRows())
      .finally(() => {
        this.createHt();
        // No scrolling as Handsontable handles this
        this.toggleBodyClass("addClass", "no-scrollbar-y");
      });

    window.addEventListener("offline", () => {
      this.isOnline = false;
    });
    window.addEventListener("online", () => {
      this.isOnline = true;
    });
  },
  created() {
    window.addEventListener("resize", _.debounce(this.getTableHeight, 100));
  },
  destroyed() {
    window.removeEventListener("resize", _.debounce(this.getTableHeight, 100));
  },
  methods: {
    getProblemRepossiDb() {
      axios.get("/api/problem-repossi-db").then(response => {
        this.problemRepossiDb = response.data;
      });
    },
    uploadingStarted() {
      this.fileManagerVisible = false;
    },
    uploadingFinished() {
      this.$bvToast.toast("Files uploading finished.", {
        title: "Success",
        autoHideDelay: 5000,
        appendToast: true,
        solid: true,
        variant: "success"
      });
    },
    toggleBodyClass(addRemoveClass, className) {
      const el = document.body;

      if (addRemoveClass === "addClass") {
        el.classList.add(className);
      } else {
        el.classList.remove(className);
      }
    },
    createHt() {
      let ctx = this;
      this.hotInstance = new Handsontable(this.$refs.ht, {});
      this.hotInstance.updateSettings({
        height: this.getTableHeight(),
        enterMoves: {
          row: 0,
          col: 1
        },
        fillHandle: {
          direction: "vertical",
          autoInsertRow: false
        },

        filters: true,
        dropdownMenu: [
          "filter_by_value",
          "filter_by_condition",
          "filter_action_bar"
        ],
        comments: true,
        data: [],
        width: "100%",
        rowHeaders: true,
        selectionMode: "range",
        invalidCellClassName: "invalidCell",
        viewportRowRenderingOffset: 50,
        viewportColumnRenderingOffsetNumber: 30,
        currentRowClassName: "currentRow",
        afterChange: this.afterChange,
        beforePaste: this.beforePaste,
        afterPaste: this.afterPaste,
        beforeRemoveRow: this.beforeRemoveRow,
        beforeAutofill: this.beforeAutofill,
        afterFilter: this.afterFilter,
        fixedColumnsLeft: 2,
        contextMenu: {
          items: {
            remove_row: {},
            separator: Handsontable.plugins.ContextMenu.SEPARATOR,
            duplicate_row: {
              name: this.$t("duplicate_row"),
              callback: this.duplicateRow
            },
            view_files: {
              name: this.$t("view_technical_sheet"),
              callback: this.downloadTechnicalSheet
            }
          }
        },
        cells: function(row, col, prop) {
          return ctx.cellHandler(this, row, col, prop);
        }
      });

      this.setColumns();
      this.hotInstance.loadData(this.data);
      this.afterInit();
    },
    /**
     * Update the column settings and headers
     **/
    setColumns() {
      this.hotInstance.updateSettings({
        columns: this.getColumnSettings(),
        colHeaders: this.getColumnHeaders()
      });

      this.tweakTable();
    },
    getColumnSettings() {
      let attrSettings = this.productAttributes.map(a =>
        this.productAttributeSettings(a.key)
      );
      let tplSettings = this.report.template.columns.map(c => {
        var column = this.customColumnSettings(c.id);

        if (column.name.match(/Stone/)) {
          column.readOnly = true;
          this.stoneColumnNames.push(column.data);
        }

        return column;
      });
      let ctx = this;
      let settings = [];

      Array.prototype.push.apply(settings, [
        {
          data: "product.reference",
          readOnly: true,
          renderer: this.referenceRenderer
        }
      ]);

      if (this.isShowSerialNumber) {
        Array.prototype.push.apply(settings, [
          {
            data: "serialNumber",
            readOnly: true
          }
        ]);
      }

      Array.prototype.push.apply(settings, [
        {
          data: "product.collection",
          readOnly: true
        },
        {
          data: "product.product",
          readOnly: true,
          autoRowSize: false,
          width: "280px"
        }
      ]);

      Array.prototype.push.apply(settings, attrSettings);

      Array.prototype.push.apply(settings, [
        {
          data: "po_number",
          readOnly: true
        },
        {
          data: "final_client",
          readOnly: true
        },
        {
          data: "po_due_date",
          readOnly: true
        }
      ]);

      Array.prototype.push.apply(settings, [
        {
          data: "remainingQuantity",
          readOnly: true,
          editor: false,
          type: "numeric"
        },
        {
          data: "calculatedRemainingQuantity",
          readOnly: true,
          editor: false,
          type: "numeric",
          width: "100px"
        },
        {
          data: "passCount",
          type: "numeric",
          validator: function(value, cb) {
            ctx.remainingQuantityValidator(this, value, cb);
          },
          allowEmpty: false,
          allowInvalid: false,
          numericFormat: {
            pattern: "0"
          }
        },
        {
          data: "failCount",
          type: "numeric",
          validator: function(value, cb) {
            cb(ctx.numericInputValidator(this, value));
          },
          allowEmpty: false,
          allowInvalid: false,
          numericFormat: {
            pattern: "0"
          }
        },
        {
          data: "repairCount",
          type: "numeric",
          validator: function(value, cb) {
            ctx.repairCountValidator(this, value, cb);
          },
          allowEmpty: false,
          allowInvalid: false,
          numericFormat: {
            pattern: "0"
          }
        },
        {
          data: "productQuantity",
          readOnly: true,
          editor: false,
          type: "numeric"
        },
        {
          data: "problemCategory",
          type: "dropdown",
          source: function(query, process) {
            ctx.getProblemCategories(this, query, process);
          },
          strict: false,
          allowInvalid: false,
          width: "170px"
        },
        {
          data: "problem",
          type: "dropdown",
          source: function(query, process) {
            ctx.getProblems(this, query, process);
          },
          strict: true,
          allowInvalid: false,
          width: "180px"
        }
      ]);
      Array.prototype.push.apply(settings, tplSettings);

      if (this.isDepartments) {
        Array.prototype.push.apply(settings, [
          {
            data: "problemDepartment",
            type: "dropdown",
            source: this.getProblemDepartments,
            strict: false,
            allowInvalid: false,
            width: "170px"
          }
        ]);
      }

      Array.prototype.push.apply(settings, [
        {
          data: "photos",
          editor: false,
          renderer: this.photosRenderer
        },
        {
          data: "comment",
          width: "300px"
        },
        {
          data: "productHistory",
          width: "300px"
        },
        {
          data: "id",
          type: "numeric"
        },
        {
          data: "temp_id"
        },
        {
          data: "purchase_order_row_id",
          type: "numeric"
        }
      ]);

      return settings;
    },
    getColumnHeaders() {
      let attrColHeaders = this.productAttributes.map(a => a.name);
      let tplColHeaders = this.report.template.columns.map(c => c.name);
      let headers = [];

      Array.prototype.push.apply(headers, [this.$t("Reference")]);

      if (this.isShowSerialNumber) {
        Array.prototype.push.apply(headers, [this.$t("Serial Number")]);
      }

      Array.prototype.push.apply(headers, [
        this.$t("Collection"),
        this.$t("Product")
      ]);

      Array.prototype.push.apply(headers, attrColHeaders);

      Array.prototype.push.apply(headers, [
        this.$t("Purchase Order"),
        this.$t("Final Client"),
        this.$t("Due date")
      ]);

      Array.prototype.push.apply(headers, [
        "HiddenRemaining", // Column exists for purpose of calculation
        this.$t("Remaining"),
        this.$t("Pass"),
        this.$t("Fail"),
        this.$t("Repair"),
        this.$t("Product Quantity"),
        this.$t("Defect Category"),
        this.$t("Defect")
      ]);
      Array.prototype.push.apply(headers, tplColHeaders);
      if (this.isDepartments) {
        Array.prototype.push.apply(headers, [this.$t("Defect Department")]);
      }

      Array.prototype.push.apply(headers, [
        this.$t("Photos"),
        this.$t("Comment"),
        this.$t("Product History"),
        "ID",
        "TempID",
        "POROW"
      ]);

      return headers;
    },
    scrollback() {
      document.querySelector(".ht_master .wtHolder").scrollLeft = 0;
    },
    afterInit() {
      this.tweakTable();
      this.runQueue();
      this.reportLoaded = true;
    },
    tweakTable() {
      this.hotInstance.updateSettings({
        // Hide ID column
        hiddenColumns: {
          columns: [
            this.hotInstance.propToCol("id"),
            this.hotInstance.propToCol("temp_id"),
            this.hotInstance.propToCol("purchase_order_row_id"),
            this.hotInstance.propToCol("remainingQuantity")
          ]
        }
      });
    },
    getTableHeight() {
      return window.innerHeight - 180;
    },
    getReport() {
      return new Promise(resolve => {
        axios
          .get("/api/reports/" + this.reportId)
          .then(response => {
            this.report = response.data.data;
            this.lastSaveDate = moment
              .utc(this.report.updated_at, "YYYY-MM-DD H:m:s")
              .local();
          })
          .finally(() => {
            resolve();
          });
      });
    },
    fetchProblems() {
      return new Promise(resolve => {
        axios
          .get("/api/reports/" + this.report.id + "/products-problems")
          .then(response => {
            this.problems = response.data.data;
            if (typeof this.problems[0].categories !== "undefined") {
              this.isDepartments = false; // todo removed this
            }
          })
          .finally(() => {
            resolve();
          });
      });
    },
    getRows() {
      return new Promise(resolve => {
        axios
          .get("/api/reports/" + this.reportId + "/rows", {})
          .then(response => {
            this.data = response.data.data;
            this.isNewReport = this.data.length <= 0;

            this.data.forEach(
              function(product) {
                if (product.serialNumber !== null) {
                  this.isShowSerialNumber = true;
                  return;
                }
              }.bind(this)
            );

            this.getUniqueProducts(response.data.data);
            if (this.hotInstance !== null) {
              this.hotInstance.loadData(response.data.data);
              this.setColumns();

              // Redo sorting
              this.applySorting();
              this.clearFilters();
            }
          })
          .finally(() => {
            resolve();
          });
      });
    },
    getUniqueProducts(rows) {
      this.products = {};
      let products = {};
      rows.forEach(r => {
        products[r.product.reference] = r.product;
      });

      this.products = products;
    },
    applySorting() {
      const sp = this.hotInstance.getPlugin("columnSorting");
      if (sp.isSorted()) {
        sp.sort(sp.getSortConfig());
      }
    },
    clearFilters() {
      const fp = this.hotInstance.getPlugin("filters");
      fp.clearConditions();
      fp.filter();
    },
    getProblemDepartments(query, process) {
      process(this.problems.map(c => c.name));
    },
    getProblemCategories(ctx, query, process) {
      if (this.isDepartments) {
        let instance = this.hotInstance;
        let department = instance.getDataAtRowProp(
          ctx.row,
          "problemDepartment"
        );
        let categories = [];

        if (department === null) {
          this.problems.forEach(c =>
            categories.push(...c.categories.map(p => p.name))
          );
        } else {
          categories = this.problems
            .find(
              c =>
                c.name ===
                instance.getDataAtRowProp(ctx.row, "problemDepartment")
            )
            .categories.map(p => p.name);
        }

        categories = categories.filter(p => p.length > 0);

        categories.sort((a, b) => {
          return a > b ? 1 : -1;
        });

        process(categories);
      } else {
        process(this.problems.map(c => c.name));
      }
    },
    getProblems(ctx, query, process) {
      if (this.isDepartments) {
        let instance = this.hotInstance;

        // If problem category is set, the available defect should change

        let cat = instance.getDataAtRowProp(ctx.row, "problemCategory");
        let problems = [];

        if (cat === null) {
          this.problems.forEach(d =>
            d.categories.forEach(c => {
              problems.push(...c.problems.map(p => p.name));
            })
          );
        } else {
          this.problems.forEach(d => {
            d.categories.forEach(c => {
              if (
                c.name === instance.getDataAtRowProp(ctx.row, "problemCategory")
              ) {
                problems = c.problems;
                return false;
              }
            });
          });
          problems = problems.map(p => p.name);
        }

        problems = problems.filter(p => p.length > 0);

        problems.sort((a, b) => {
          return a > b ? 1 : -1;
        });

        process(problems);
      } else {
        let instance = this.hotInstance;

        // If problem category is set, the available defect should change

        let cat = instance.getDataAtRowProp(ctx.row, "problemCategory");
        let problems = [];

        if (cat === null) {
          return [];
        } else {
          problems = this.problems
            .find(
              c =>
                c.name === instance.getDataAtRowProp(ctx.row, "problemCategory")
            )
            .problems.map(p => p.name);
        }

        problems = problems.filter(p => p.length > 0);

        problems.sort((a, b) => {
          return a > b ? 1 : -1;
        });

        process(problems);
      }
    },
    productAttributeSettings(attrId) {
      return {
        data: "product.product_attributes." + attrId + ".value",
        readOnly: true
      };
    },
    customColumnSettings(columnId) {
      let column = this.report.template.columns.find(c => c.id === columnId);

      if (!column) {
        return {};
      }

      let generalSettings = {
        name: column.name,
        data: "customColumn" + column.id,
        allowEmpty: column.required
      };

      let typeSettings = {};

      switch (column.type) {
        case "option":
          typeSettings = {
            type: "dropdown",
            source: column.options.map(o => o.name)
          };
          break;

        case "bool":
          typeSettings = {
            type: "checkbox"
          };
          break;

        case "int":
          typeSettings = {
            type: "numeric",
            validator: "numeric"
          };
          break;

        case "float":
          typeSettings = {
            type: "numeric",
            numericFormat: {
              pattern: "0,0.00"
            },
            validator: "numeric"
          };
          break;

        case "date":
          typeSettings = {
            type: "date",
            dateFormat: "MM/DD/YY",
            defaultDate: moment()
          };
          break;
      }
      return { ...generalSettings, ...typeSettings };
    },
    duplicateRow(action, coords, e) {
      let hot = this.hotInstance;
      let rowIndex = coords[0].start.row;
      let nextRow = rowIndex + 1;
      let selectedData = hot.getDataAtRow(rowIndex);
      let colIndex;
      let columnsToReset = [
        ["id", null],
        ["passCount", 0],
        ["failCount", 0],
        ["repairCount", 0],
        ["problemCategory", null],
        ["problem", null],
        ["comment", null],
        ["temp_id", uuidv4()]
      ];

      // Insert one new row below
      hot.alter("insert_row", nextRow, 1);

      // Reset data
      columnsToReset.forEach(function(el) {
        colIndex = hot.propToCol(el[0]);
        selectedData[colIndex] = el[1];
      });

      // We want to populate from array, but this does not work on readOnly columns
      // here is an ugly solution that temporarily disables the readOnly state,
      // populates the row, and finally resets the readOnly state
      let readonlyCols = Object.keys(hot.getDataAtRow(nextRow)).filter(
        k => hot.getCellMeta(nextRow, k).readOnly
      );

      readonlyCols.forEach(k => hot.setCellMeta(nextRow, k, "readOnly", false));

      hot.populateFromArray(nextRow, 0, [selectedData]);

      readonlyCols.forEach(k => hot.setCellMeta(nextRow, k, "readOnly", true));

      this.saveRow(nextRow);
    },
    downloadTechnicalSheet(action, coords, e) {
      // Get the collection -> base product
      let i = this.hotInstance;
      let row = coords[0].start.row;
      let product = i.getDataAtRowProp(row, "product");

      if (!product || !product.files.length) {
        this.triggerNoTechnicalSheetModal(
          product.collection + " " + product.product
        );
      }
      product.files.forEach(f => {
        window.open(f.url);
      });
    },
    triggerNoTechnicalSheetModal(product) {
      this.$bvModal.msgBoxOk(
        `There was no technical sheet uploaded for ${product}`,
        {
          title: "Technical sheet missing",
          size: "sm",
          buttonSize: "sm",
          okVariant: "success"
        }
      );
    },
    setStoneCertificates() {
      let instance = this.hotInstance;

      for (let i = 0; i < instance.countRows(); i++) {
        if (
          this.data[i].certificates.length > 0 &&
          this.stoneColumnNames.length > 0
        ) {
          for (let j = 0; j < this.data[i].certificates.length; j++) {
            if (typeof this.stoneColumnNames[j] !== "undefined") {
              instance.setDataAtRowProp(
                i,
                this.stoneColumnNames[j],
                this.data[i].certificates[j]
              );
            }
          }
        }
      }
    },
    calculateRemaining(refPo) {
      let instance = this.hotInstance;
      let changes = [];
      for (let i = 0; i < instance.countRows(); i++) {
        // Get the PO id and get the total number of checked items in this report for that PO id

        let po_id = instance.getDataAtRowProp(i, "purchase_order_row_id");

        if (refPo !== undefined && refPo !== po_id) {
          continue;
        }

        let remainder =
          instance.getDataAtRowProp(i, "remainingQuantity") -
          this.data
            .filter(d => d.purchase_order_row_id === po_id)
            .reduce(
              (a, v) =>
                a +
                parseInt(v.passCount ? v.passCount : 0) +
                parseInt(v.repairCount ? v.repairCount : 0),
              0
            );
        changes.push([i, "calculatedRemainingQuantity", remainder]);
      }

      instance.setDataAtRowProp(changes);
    },
    afterChange(changes, source) {
      let i = this.hotInstance;
      if (this.isPasting) {
        return;
      }

      if (source === "Autofill.fill") {
        return;
      }

      let quantityCols = ["passCount", "failCount", "repairCount"];

      if (source === "loadData" || source === "populateFromArray") {
        this.calcSuccessRate();
        this.calculateRemaining();
        this.setStoneCertificates();
      }

      if (source === "edit") {
        changes.forEach(([row, prop, oldValue, newValue]) => {
          if (oldValue === newValue || prop === "id" || prop === "photos") {
            return;
          }

          if (prop === "calculatedRemainingQuantity") {
            return;
          }

          if (prop === "passCount" || prop === "repairCount") {
            this.calculateRemaining(
              i.getDataAtRowProp(row, "purchase_order_row_id")
            );
          }

          /// Problem category changed, need to reset the problem
          if (prop === "problemCategory") {
            if (
              i.getDataAtRowProp(row, "problemCategory") !==
              this.getCategoryNameForProblem(i.getDataAtRowProp(row, "problem"))
            ) {
              i.setDataAtRowProp(row, "problem", null);
            }
            // this.setTopLevelProblemData(row)
          }

          if (prop === "problem") {
            // If a problem is selected, but the problem is not in the same category as the selected category
            // we need to change the category
            // this.setProblemCategoryForProblem(row);
            this.setTopLevelProblemData(row);
          }

          if (quantityCols.includes(prop)) {
            this.calcSuccessRate();
          }

          this.saveRow(row);
        });
      }
    },
    beforeAutofill(start, end, data) {
      return false;
    },

    // Batch save rows saves multiple rows at once and then reloads the report data
    // Takes in an array of rows to be saved
    batchSaveRows: _.debounce(function(rows) {
      let saveRows = [];
      rows.forEach(r => this.saveRow(r));
    }, 200),
    setTopLevelProblemData(row) {
      if (
        this.hotInstance.getDataAtRowProp(row, "problemCategory") !== null &&
        this.hotInstance.getDataAtRowProp(row, "problem") !== null
      ) {
        let categoryId = null,
          problemId = null;

        this.problems.forEach(category => {
          if (
            category.name ===
            this.hotInstance.getDataAtRowProp(row, "problemCategory")
          ) {
            categoryId = category.id;

            category.problems.forEach(problem => {
              if (
                problem.name ===
                this.hotInstance.getDataAtRowProp(row, "problem")
              ) {
                problemId = problem.id;
              }
            });
          }
        });

        if (categoryId !== null && problemId !== null) {
          let departmentId = null,
            repossiTriptyqueId = null;

          this.problemRepossiDb.forEach(data => {
            if (
              data.problem_category_id === categoryId &&
              data.problem_id === problemId
            ) {
              departmentId = data.problem_department_id;
              repossiTriptyqueId = data.repossi_triptyque_id;
            }
          });

          if (departmentId !== null && repossiTriptyqueId !== null) {
            let repossiTriptyqueColumn = this.report.template.columns.filter(
              c => c.name === "Repossi Triptyque"
            );
            let problemDepartmentColumn = this.report.template.columns.filter(
              c => c.name === "Repossi Catégorie"
            );

            if (
              typeof repossiTriptyqueColumn[0] !== "undefined" &&
              typeof problemDepartmentColumn[0] !== "undefined"
            ) {
              let repossiVal = null,
                departmentVal = null;
              repossiTriptyqueColumn[0].options.forEach(o => {
                if (o.id === repossiTriptyqueId) {
                  repossiVal = o.name;
                }
              });

              problemDepartmentColumn[0].options.forEach(o => {
                if (o.id === departmentId) {
                  departmentVal = o.name;
                }
              });

              if (
                repossiTriptyqueColumn !== null &&
                problemDepartmentColumn !== null
              ) {
                this.hotInstance.setDataAtRowProp(
                  row,
                  "customColumn" + repossiTriptyqueColumn[0].id,
                  repossiVal
                );

                this.hotInstance.setDataAtRowProp(
                  row,
                  "customColumn" + problemDepartmentColumn[0].id,
                  departmentVal
                );
              }
            }
          }
        }
      }
    },
    getDepartmentNameForCategory(category) {
      if (category === null) {
        return null;
      }

      let department = null;

      this.problems.forEach(d => {
        if (department !== null) {
          return;
        }
        if (d.categories.find(c => c.name === category)) {
          department = d;
        }
      });

      if (department === null) {
        return null;
      }
      return department;
    },
    setProblemCategoryForProblem(row) {
      let problem = this.hotInstance.getDataAtRowProp(row, "problem");

      if (problem === null) {
        return;
      }

      let currentCategory = this.hotInstance.getDataAtRowProp(
        row,
        "problemCategory"
      );

      let category = this.getCategoryNameForProblem(problem);

      if (category !== null && currentCategory !== category) {
        this.hotInstance.setDataAtRowProp(row, "problemCategory", category);
      }
    },
    getCategoryNameForProblem(problem) {
      if (problem === null) {
        return null;
      }

      let category = null;

      if (this.isDepartments) {
        this.problems.forEach(d => {
          d.categories.forEach(c => {
            if (category !== null) {
              return;
            }
            if (c.problems.find(p => p.name === problem)) {
              category = c;
            }
          });
        });

        if (category === null) {
          return null;
        }
        return category.name;
      } else {
        this.problems.forEach(c => {
          if (category !== null) {
            return;
          }
          if (c.problems.find(p => p.name === problem)) {
            category = c;
          }
        });

        return category !== null ? category.name : null;
      }
    },
    cellHandler: function(ctx, row, col, prop) {
      let cellProps = {};

      if (prop === "productHistory") {
        cellProps.renderer = this.historyRendering;
      }

      if (prop === "failCount") {
        cellProps.renderer = this.failCountRendering;
      }

      // Add the comment to the PO row
      if (prop === "po_number") {
        let data = this.data[this.hotInstance.toPhysicalRow(row)];
        if (data !== undefined && data.po_row_comment) {
          cellProps.comment = { value: data.po_row_comment };
        }
      }

      if (prop === "repairCount") {
        cellProps.renderer = this.repairCountRendering;
      }

      return cellProps;
    },
    failCountRendering(instance, td, row, col, prop, value, cellProperties) {
      Handsontable.renderers.TextRenderer.apply(this, arguments);
      if (parseInt(value) > 0) {
        td.className = "has-fail-count";
      }
    },
    referenceRendering(instance, td, row, col, prop, value, cellProperties) {
      Handsontable.renderers.TextRenderer.apply(this, arguments);
      td.className = "has-failures";
    },
    historyRendering(instance, td, row, col, prop, value, cellProperties) {
      Handsontable.renderers.TextRenderer.apply(this, arguments);
      if (value !== "") {
        instance.getCellMeta(row, 0).renderer = this.referenceRendering;
      }
    },
    repairCountRendering(instance, td, row, col, prop, value, cellProperties) {
      Handsontable.renderers.TextRenderer.apply(this, arguments);
      if (parseInt(value) > 0) {
        td.className = "has-repair-count";
      }
    },
    calcSuccessRate() {
      let i = this.hotInstance;

      let passColumnIndex = i.propToCol("passCount");
      let failColumnIndex = i.propToCol("failCount");
      let repairColumnIndex = i.propToCol("repairCount");

      this.pass = i
        .getSourceDataAtCol(passColumnIndex)
        .reduce((a, b) => a + b, 0);
      this.fail = i
        .getSourceDataAtCol(failColumnIndex)
        .reduce((a, b) => a + b, 0);
      this.repair = i
        .getSourceDataAtCol(repairColumnIndex)
        .reduce((a, b) => a + b, 0);

      let totalQuantity = this.pass + this.repair + (this.fail - this.repair);
      this.successRate = ((this.pass + this.repair) / totalQuantity) * 100;
    },
    numericInputValidator(ctx, value) {
      const commentPlugin = this.hotInstance.getPlugin("comments");
      let row = this.hotInstance.toVisualRow(ctx.row);
      if (!Number.isInteger(value)) {
        commentPlugin.setCommentAtCell(
          row,
          ctx.col,
          `The value must be a non-decimal number`
        );
        commentPlugin.show();
        return false;
      } else {
        commentPlugin.removeCommentAtCell(row, ctx.col);
        return true;
      }
    },
    remainingQuantityValidator(ctx, value, cb) {
      if (!this.numericInputValidator(ctx, value)) {
        cb(false);
        return;
      }

      let row = this.hotInstance.toVisualRow(ctx.row);

      // First of all, the input has to be numeric
      if (Number.isNaN(value)) {
        cb(false);
        return false;
      }

      let currentValue = this.hotInstance.getDataAtCell(row, ctx.col);
      let remainingQuantity = this.hotInstance.getDataAtRowProp(
        row,
        "calculatedRemainingQuantity"
      );
      let diff = value - currentValue;

      // Next, we need to check if the pass + repair count subtracted from the remaining quantity is
      // less than 0, if that is the case, validation fails and the input value is too high
      let remaining = remainingQuantity - diff;
      let max = remainingQuantity + currentValue;

      const commentPlugin = this.hotInstance.getPlugin("comments");
      if (remaining < 0) {
        commentPlugin.setCommentAtCell(
          row,
          ctx.col,
          `The maximum value for this cell is ${max} because it can not exceed the remaining quantity`
        );
        commentPlugin.show();
      } else {
        commentPlugin.removeCommentAtCell(row, ctx.col);
      }

      cb(remaining >= 0);

      return remaining >= 0;
    },
    repairCountValidator(ctx, value, cb) {
      let row = this.hotInstance.toVisualRow(ctx.row);
      if (!this.numericInputValidator(ctx, value)) {
        cb(false);
        return;
      }

      if (Number.isNaN(value)) {
        cb(false);
      }

      // Repair count should never be bigger than the fail count
      let failCount = this.hotInstance.getDataAtRowProp(row, "failCount");

      if (failCount === null) {
        failCount = 0;
      }

      // Also, repair count + pass count can not be greater than

      const success = parseInt(value) <= parseInt(failCount);

      const commentPlugin = this.hotInstance.getPlugin("comments");

      if (!success) {
        commentPlugin.setCommentAtCell(
          row,
          ctx.col,
          `The repair count can not exceed the fail count of ${parseInt(
            failCount
          )}`
        );
        commentPlugin.show();
        cb(false);
      } else if (this.remainingQuantityValidator(ctx, value, cb)) {
        commentPlugin.removeCommentAtCell(row, ctx.col);
      }
    },
    beforeRemoveRow(index, amount, physicalRows) {
      physicalRows.forEach(i => {
        this.deleteRow(i);
      });
    },
    beforePaste(data, coords) {
      // Only specific columns need actions
      // always save after paste
      let d = [];
      let col = this.hotInstance.colToProp(coords[0].startCol);

      this.hotInstance.setDataAtCell(d);
      this.hotInstance.render();
    },
    afterPaste(data, coords) {
      let rows = [];
      for (let i = 0; i < data.length; i++) {
        rows.push(coords[0]["startRow"] + i);
      }
      this.batchSaveRows(rows);
    },
    getProblemIdOfSelectedCategory(categoryName, problemName) {
      let category = this.problems.find(c => c.name === categoryName);

      if (category === null || typeof category === "undefined") {
        return null;
      }

      let prob = category.problems.find(p => p.name === problemName);

      return prob ? prob.id : null;
    },
    getProblemId(name) {
      let category = this.getCategoryNameForProblem(name);

      let cat = null;
      if (this.isDepartments) {
        this.problems.forEach(d => {
          d.categories.forEach(c => {
            if (c.name === category) {
              cat = c;
              return false;
            }
          });
        });
      } else {
        cat = this.problems.find(c => c.name === category);
      }

      if (!cat) {
        return null;
      }

      let prob = cat.problems.find(p => p.name === name);

      return prob ? prob.id : null;
    },
    postSync(additionCount) {
      // No rows have been added, so we don't need to re-fetch rows
      if (additionCount === 0) {
        return;
      }

      this.getRows();
      R;
      this.lastSaveDate = moment();
    },
    getRowData(row) {
      let i = this.hotInstance;

      let data = {};
      data.productReference = i.getDataAtRowProp(row, "product.reference");

      data.purchase_order_row_id = i.getDataAtRowProp(
        row,
        "purchase_order_row_id"
      );

      // The product reference is mandatory
      if (!data.productReference || !data.purchase_order_row_id) {
        return null;
      }

      data.passCount = i.getDataAtRowProp(row, "passCount");
      data.failCount = i.getDataAtRowProp(row, "failCount");
      data.repairCount = i.getDataAtRowProp(row, "repairCount");

      data.custom = {};
      data.comment = i.getDataAtRowProp(row, "comment");

      data.problem_id = this.getProblemId(i.getDataAtRowProp(row, "problem"));

      data.problem_id = this.getProblemIdOfSelectedCategory(
        i.getDataAtRowProp(row, "problemCategory"),
        i.getDataAtRowProp(row, "problem")
      );

      this.report.template.columns.forEach(c => {
        let v = i.getDataAtRowProp(row, "customColumn" + c.id);
        if (v !== null) {
          data.custom[c.id] = { id: c.id, value: v };
        }
      });
      return data;
    },
    saveRow: memoizeDebounce(function(row) {
      let i = this.hotInstance;
      let data = this.getRowData(row);

      if (data === null) {
        return;
      }

      // Check what the current sorting state is
      const columnSortPlugin = i.getPlugin("columnSorting");
      columnSortPlugin.sort(columnSortPlugin.getSortConfig());

      // We need to figure out if the row has an ID
      // If the row does not have an ID we need to get the temporary_id meta

      let cmd = {
        method: "post",
        data: data,
        id: i.getDataAtRowProp(row, "id"),
        temp_id: i.getDataAtRowProp(row, "temp_id"),
        callback: (response, visualRow) => {
          let current_id = i.getDataAtRowProp(visualRow, "id");
          if (current_id) {
            return;
          }

          let id = response.data.data.id;
          i.setDataAtRowProp(row, "id", id);
        }
      };

      this.enqueueCommand(cmd);
    }, 500),
    deleteRow(physicalRowId) {
      let i = this.hotInstance;
      let id = i.getDataAtRowProp(i.toVisualRow(physicalRowId), "id");

      if (id <= 0) {
        return;
      }

      this.enqueueCommand({
        method: "delete",
        id: id
      });
    },
    enqueueCommand(cmd) {
      let baseCmd = {
        method: null,
        id: null,
        temp_id: null,
        data: null,
        callback: () => {},
        retries: 0,
        attempts: 0
      };

      commandStack.enqueue(Object.assign(baseCmd, cmd));
      this.queueLength = commandStack.count();
    },
    /**
     *  Todo: Optimize the queries to do multiple commands at once and only send the last state instead of sending all queued changes
     *
     * @returns {Promise<void>}
     */
    async runQueue() {
      const delay = ms => new Promise(res => setTimeout(res, ms));
      const i = this.hotInstance;
      // Check for items in the queue
      let item;
      let b = true;

      while (b) {
        // Get a new item
        item = await commandStack.waitForItem();

        if (!this.isOnline) {
          await delay(500);
          continue;
        }

        // this is not great...
        let id = item.id > 0 ? item.id : this.getIdforTempId(item.temp_id);

        // Delete can only be done if the row has an ID and exists
        if (id === undefined || (item.method === "delete" && id <= 0)) {
          commandStack.dequeue();
          this.queueLength = commandStack.count();
          await delay(500);
          continue;
        }

        // Determine if the row already has an id
        // If the row has no id, it is a new row
        let url =
          id > 0
            ? `/api/reports/${this.report.id}/rows/${id}`
            : `/api/reports/${this.report.id}/rows`;

        this.savingRow = true;
        await axios({
          method: item.method,
          url: url,
          data: item.data
        })
          .then(response => {
            this.lastSaveDate = moment();
            if (item.callback !== undefined) {
              let rowIndex = item.temp_id
                ? this.getVisualRowIndexByTempId(item.temp_id)
                : this.getVisualRowIndexById(item.id);

              item.callback(response, rowIndex);
            }
            commandStack.dequeue();
            return delay(500);
          })
          .catch(err => {
            if (!err.response || !err.response.status) {
              item.retries += 1;
              // @todo implement back off
              return delay(1000);
            } else {
              this.$bvToast.toast(
                this.$t("queue_error", {
                  error: err.response.status
                }),
                {
                  title: this.$t("something_went_wrong"),
                  variant: "warning",
                  autoHideDelay: 4000,
                  appendToast: true
                }
              );
              commandStack.dequeue();
            }
          })
          .finally(() => {
            this.savingRow = false;
            this.queueLength = commandStack.count();
          });
      }
    },
    getIdforTempId(tempId) {
      let idx = this.getVisualRowIndexByTempId(tempId);

      if (idx === -1) {
        return undefined;
      }

      let i = this.hotInstance;

      return i.getDataAtRowProp(idx, "id");
    },
    getVisualRowIndexByTempId(tempId) {
      if (tempId === undefined || tempId === null) {
        return -1;
      }

      let i = this.hotInstance;
      let tmpIdx = i.propToCol("temp_id");
      return i.getData().findIndex(r => r[tmpIdx] === tempId);
    },
    getVisualRowIndexById(id) {
      if (id === undefined || id === null) {
        return -1;
      }

      let i = this.hotInstance;
      let idIdx = i.propToCol("id");
      return i.getData().findIndex(r => r[idIdx] === id);
    },
    // Renderers
    referenceRenderer(instance, td, row, col, prop, value, cellProperties) {
      let product = this.products[value];
      Handsontable.dom.addClass(td, "reference");
      // If no files, just render default
      if (!product || !product.files.length) {
        Handsontable.dom.addClass(td, "has-no-files");
        Handsontable.renderers.TextRenderer.apply(this, arguments);
        return;
      }

      Handsontable.dom.addClass(td, "has-files");
      Handsontable.dom.empty(td);
      let link = document.createElement("a");
      link.addEventListener("click", () => {
        window.open(
          `/smart-vault/${product.base_product_id}/${product.variant_id}`,
          "_blank"
        );
      });

      link.append(document.createTextNode(value));
      td.appendChild(link);
    },
    photosRenderer(instance, td, row, col, prop, value, cellProperties) {
      Handsontable.dom.empty(td);
      Handsontable.dom.addClass(td, "photos");
      Handsontable.dom.addClass(
        td,
        parseInt(value) > 0 ? "has-photos" : "has-no-photos"
      );
      let link = document.createElement("a");
      link.append(document.createTextNode(value));
      link.addEventListener("click", () => {
        this.openManager(row);
      });
      td.appendChild(link);
    },
    openManager(row) {
      let id = this.hotInstance.getDataAtRowProp(row, "id");
      this.fileManagerTitle = "Row #" + id;
      this.fileManagerResourceId = id;
      this.fileManagerVisible = true;
      this.fileManagerRow = row;
    },
    filesUpdated(files) {
      this.hotInstance.setDataAtRowProp(
        this.fileManagerRow,
        "photos",
        files.length
      );
    },
    afterFilter(conditionsStack) {
      this.activeFilters = conditionsStack.length;
    }
  }
};
</script>

<style>
.handsontable td.invalidCell {
  background-color: rgba(255, 0, 0, 0.11);
}

.handsontable td.has-fail-count {
  color: #b20000;
  font-weight: bold;
}

.handsontable td.has-repair-count {
  color: #008000;
  font-weight: bold;
}

.handsontable .currentRow {
  background: rgba(13, 131, 221, 0.1);
}

.has-failures {
  background: #ffdf7e !important;
}
</style>
